import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Topbar from "../../../../components/topbar/TopBar";
import { createTarifaEnterpriseUseCase } from "../domain/usecases/createTarifaEnterpriseUseCase";
import { getTarifaEnterpriseByIdUseCase } from "../domain/usecases/getTarifaEnterpriseByIdUseCase";
import { updateTarifaEnterpriseUseCase } from "../domain/usecases/updateTarifaEnterpriseUseCase";

function createEmptyForm() {
  return {
    priceBase: "",
    priceDistance: "",
    priceDuration: "",
    priceDelay: "",
  };
}

function createPayload(form, enterpriseId) {
  return {
    enterpriseId: Number(enterpriseId),
    priceBase: Number(form.priceBase),
    priceDistance: Number(form.priceDistance),
    priceDuration: Number(form.priceDuration),
    priceDelay: Number(form.priceDelay),
  };
}

function createFormFromRecord(record) {
  if (!record) {
    return createEmptyForm();
  }

  return {
    priceBase:
      record.priceBase === null || record.priceBase === undefined
        ? ""
        : String(record.priceBase),
    priceDistance:
      record.priceDistance === null || record.priceDistance === undefined
        ? ""
        : String(record.priceDistance),
    priceDuration:
      record.priceDuration === null || record.priceDuration === undefined
        ? ""
        : String(record.priceDuration),
    priceDelay:
      record.priceDelay === null || record.priceDelay === undefined
        ? ""
        : String(record.priceDelay),
  };
}

function isFormReady(form) {
  return (
    form.priceBase !== "" &&
    !Number.isNaN(Number(form.priceBase)) &&
    Number(form.priceBase) >= 0 &&
    form.priceDistance !== "" &&
    !Number.isNaN(Number(form.priceDistance)) &&
    Number(form.priceDistance) >= 0 &&
    form.priceDuration !== "" &&
    !Number.isNaN(Number(form.priceDuration)) &&
    Number(form.priceDuration) >= 0 &&
    form.priceDelay !== "" &&
    !Number.isNaN(Number(form.priceDelay)) &&
    Number(form.priceDelay) >= 0
  );
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return response?.success === true || message.includes("success");
}

export const TarifaEnterpriseCreatePage = () => {
  const { id, name } = useParams();
  const [form, setForm] = useState(createEmptyForm());
  const [currentRate, setCurrentRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const canSubmit = useMemo(() => isFormReady(form), [form]);
  const isGlobalRate = currentRate?.enterpriseId === null;
  const isUpdateMode = Boolean(currentRate?.enterpriseId);

  const loadEnterpriseRate = useCallback(async () => {
    try {
      setLoading(true);
      const rate = await getTarifaEnterpriseByIdUseCase(id);

      setCurrentRate(rate);
      setForm(createFormFromRecord(rate));
    } catch (error) {
      setCurrentRate(null);
      setForm(createEmptyForm());
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo cargar la tarifa empresarial.",
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadEnterpriseRate();
  }, [loadEnterpriseRate]);

  const handleChange = (event) => {
    const { name: fieldName, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSave = async () => {
    const payload = createPayload(form, id);

    try {
      setSaving(true);
      const response = isUpdateMode
        ? await updateTarifaEnterpriseUseCase({
            id: currentRate.id,
            ...payload,
          })
        : await createTarifaEnterpriseUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error(
          isUpdateMode
            ? "No se pudo actualizar la tarifa empresarial."
            : "No se pudo crear la tarifa empresarial.",
        );
      }

      setMessage({
        open: true,
        type: "success",
        text: isUpdateMode
          ? "Tarifa empresarial actualizada correctamente."
          : "Tarifa empresarial creada correctamente.",
      });
      await loadEnterpriseRate();
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text:
          error.message ||
          (isUpdateMode
            ? "No se pudo actualizar la tarifa empresarial."
            : "No se pudo crear la tarifa empresarial."),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": { color: "#000" },
            p: 1,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 760,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1, md: 2 },
                  py: { xs: 2, md: 2 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={800}
                  sx={{ color: "#fff !important" }}
                >
                  {isUpdateMode
                    ? "ACTUALIZAR TARIFA PARA EMPRESA"
                    : "CREAR TARIFA PARA EMPRESA"}
                </Typography>
                {name && (
                  <Typography sx={{ mt: 0.75, color: "#fff !important" }}>
                    {name}
                  </Typography>
                )}
              </Box>

              <Box sx={{ p: { xs: 1, md: 3 } }}>
                <Paper
                  elevation={0}
                  sx={{
                    mx: "auto",
                    width: "100%",
                    maxWidth: 480,
                    borderRadius: 3,
                    border: "1px solid rgba(20, 83, 45, 0.12)",
                    p: { xs: 1, md: 2 },
                  }}
                >
                  {loading ? (
                    <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
                      <CircularProgress />
                      <Typography color="text.secondary">
                        Cargando tarifa empresarial...
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      {isGlobalRate && (
                        <Alert severity="warning">
                          Esta Tarifa es Global.
                        </Alert>
                      )}
                      <TextField
                        fullWidth
                        type="number"
                        label="Precio Base"
                        name="priceBase"
                        value={form.priceBase}
                        onChange={handleChange}
                        inputProps={{ min: "0", step: "0.01" }}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Precio Distancia"
                        name="priceDistance"
                        value={form.priceDistance}
                        onChange={handleChange}
                        inputProps={{ min: "0", step: "0.01" }}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Precio Duracion"
                        name="priceDuration"
                        value={form.priceDuration}
                        onChange={handleChange}
                        inputProps={{ min: "0", step: "0.01" }}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Precio Espera"
                        name="priceDelay"
                        value={form.priceDelay}
                        onChange={handleChange}
                        inputProps={{ min: "0", step: "0.01" }}
                      />
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={!canSubmit || saving}
                        sx={{
                          mt: 1,
                          minHeight: 50,
                          borderRadius: 2,
                          textTransform: "uppercase",
                          fontWeight: 700,
                          bgcolor: "#165c53",
                          color: "#fff",
                          "&:hover": {
                            bgcolor: "#12483f",
                          },
                        }}
                      >
                        {saving
                          ? isUpdateMode
                            ? "ACTUALIZANDO..."
                            : "CREANDO..."
                          : isUpdateMode
                            ? "ACTUALIZAR"
                            : "CREAR"}
                      </Button>
                    </Stack>
                  )}
                </Paper>
              </Box>
            </Paper>
          </Stack>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
};
